import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import {
  Gear,
  ShieldLock,
  Plug,
  CalendarEvent,
  PersonCircle,
  Sliders,
  ArrowRight,
} from 'react-bootstrap-icons';

function Settings({ role }) {
  const pageStyle = {
    minHeight: '100vh',
    background:
      'radial-gradient(circle at top left, rgba(59,130,246,0.20), transparent 32%), radial-gradient(circle at top right, rgba(34,197,94,0.12), transparent 28%), linear-gradient(180deg, #020617 0%, #0f172a 42%, #111827 100%)',
    color: '#e5e7eb',
  };

  const shellStyle = {
    maxWidth: 1300,
    margin: '0 auto',
  };

  const cardStyle = {
    background: 'rgba(15, 23, 42, 0.86)',
    border: '1px solid rgba(148, 163, 184, 0.16)',
    borderRadius: 22,
    color: '#e5e7eb',
    boxShadow: '0 18px 45px rgba(0,0,0,0.28)',
    backdropFilter: 'blur(10px)',
    overflow: 'hidden',
  };

  const cardSoftStyle = {
    background: 'rgba(30, 41, 59, 0.82)',
    border: '1px solid rgba(148, 163, 184, 0.14)',
    borderRadius: 20,
    color: '#e5e7eb',
  };

  const heroStyle = {
    background:
      'linear-gradient(135deg, rgba(37,99,235,0.35), rgba(15,23,42,0.94) 42%, rgba(34,197,94,0.20))',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    borderRadius: 28,
    color: '#f8fafc',
    boxShadow: '0 24px 70px rgba(0,0,0,0.35)',
  };

  const mutedStyle = {
    color: '#94a3b8',
  };

  const pillStyle = {
    border: '1px solid rgba(148, 163, 184, 0.20)',
    background: 'rgba(15, 23, 42, 0.72)',
    color: '#cbd5e1',
    borderRadius: 999,
    padding: '6px 11px',
    fontSize: 12,
    fontWeight: 700,
  };

  const SettingCard = ({ title, subtitle, icon, accent = '#38bdf8', children }) => {
    return (
      <div
        className="h-100"
        style={{
          ...cardStyle,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: `radial-gradient(circle at top right, ${accent}33, transparent 38%)`,
            pointerEvents: 'none',
          }}
        />

        <div className="p-4 position-relative">
          <div className="d-flex align-items-start gap-3 mb-3">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 50,
                height: 50,
                minWidth: 50,
                borderRadius: 16,
                background: `${accent}22`,
                border: `1px solid ${accent}35`,
                color: accent,
                fontSize: 24,
              }}
            >
              {icon}
            </div>

            <div>
              <h4 className="mb-1" style={{ color: '#f8fafc', fontWeight: 900 }}>
                {title}
              </h4>
              {subtitle && (
                <p className="mb-0" style={{ ...mutedStyle, fontSize: 14 }}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          <div className="d-grid gap-2">{children}</div>
        </div>
      </div>
    );
  };

  const SettingLink = ({ to, title, subtitle, icon, accent = '#38bdf8' }) => {
    return (
      <Link
        to={to}
        className="text-decoration-none"
        style={{
          display: 'block',
          borderRadius: 16,
          background: 'rgba(2, 6, 23, 0.42)',
          border: '1px solid rgba(148, 163, 184, 0.12)',
          color: '#e5e7eb',
          padding: 14,
          transition: 'all 160ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = `${accent}66`;
          e.currentTarget.style.background = `${accent}18`;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'rgba(148, 163, 184, 0.12)';
          e.currentTarget.style.background = 'rgba(2, 6, 23, 0.42)';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-start gap-3">
            <div
              className="d-flex align-items-center justify-content-center"
              style={{
                width: 38,
                height: 38,
                minWidth: 38,
                borderRadius: 13,
                background: `${accent}22`,
                border: `1px solid ${accent}35`,
                color: accent,
                fontSize: 18,
              }}
            >
              {icon}
            </div>

            <div>
              <div style={{ color: '#f8fafc', fontWeight: 850, lineHeight: 1.2 }}>
                {title}
              </div>

              {subtitle && (
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
                  {subtitle}
                </div>
              )}
            </div>
          </div>

          <ArrowRight style={{ color: '#94a3b8', minWidth: 18 }} />
        </div>
      </Link>
    );
  };

  return (
    <div style={pageStyle}>
      <style>
        {`
          .settings-page a:hover {
            text-decoration: none !important;
          }
        `}
      </style>

      <Container fluid className="py-4 settings-page" style={shellStyle}>
        <div className="p-4 p-lg-5 mb-4" style={heroStyle}>
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-4">
            <div>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span style={{ ...pillStyle, color: '#bfdbfe', borderColor: 'rgba(59,130,246,0.38)' }}>
                  Settings
                </span>

                {role === 'OWNER' && (
                  <span style={{ ...pillStyle, color: '#fecaca', borderColor: 'rgba(239,68,68,0.38)' }}>
                    Owner Access
                  </span>
                )}

                <span style={{ ...pillStyle, color: '#bbf7d0', borderColor: 'rgba(34,197,94,0.38)' }}>
                  Integrations
                </span>

                <span style={{ ...pillStyle, color: '#fef3c7', borderColor: 'rgba(245,158,11,0.38)' }}>
                  Notifications
                </span>
              </div>

              <h1
                className="mb-3"
                style={{
                  color: '#f8fafc',
                  fontWeight: 950,
                  letterSpacing: '-0.04em',
                  fontSize: 'clamp(2rem, 4vw, 4.35rem)',
                  lineHeight: 0.95,
                }}
              >
                Settings Control Center
              </h1>

              <p className="mb-0" style={{ color: '#cbd5e1', maxWidth: 850, fontSize: 17 }}>
                Manage users, integrations, profile settings, preferences, calendar events,
                and reminder notifications from one clean settings hub.
              </p>
            </div>

            <div
              className="p-3"
              style={{
                ...cardSoftStyle,
                minWidth: 280,
              }}
            >
              <div style={{ color: '#94a3b8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Current Role
              </div>

              <div className="mt-1" style={{ color: '#f8fafc', fontSize: 28, fontWeight: 900 }}>
                {role || 'User'}
              </div>

              <div className="mt-2" style={{ color: '#94a3b8', fontSize: 13 }}>
                {role === 'OWNER'
                  ? 'You have access to admin settings.'
                  : 'Admin settings are only visible to owner accounts.'}
              </div>
            </div>
          </div>
        </div>

        <Row className="g-4">
          {role === 'OWNER' && (
            <Col lg={6}>
              <SettingCard
                title="Admin Settings"
                subtitle="Manage team access and user permissions."
                icon={<ShieldLock />}
                accent="#ef4444"
              >
                <SettingLink
                  to="/settings/admin"
                  title="Manage Users"
                  subtitle="Add, edit, or remove dashboard users."
                  icon={<ShieldLock />}
                  accent="#ef4444"
                />
              </SettingCard>
            </Col>
          )}

          <Col lg={6}>
            <SettingCard
              title="Connected Accounts"
              subtitle="Manage marketplace, API, and integration connections."
              icon={<Plug />}
              accent="#22c55e"
            >
              <SettingLink
                to="/settings/connected-accounts"
                title="Manage Integrations"
                subtitle="Connect or review linked services."
                icon={<Plug />}
                accent="#22c55e"
              />
            </SettingCard>
          </Col>

          <Col lg={6}>
            <SettingCard
              title="Personal Settings"
              subtitle="Update profile details and dashboard preferences."
              icon={<Gear />}
              accent="#38bdf8"
            >
              <SettingLink
                to="/settings/profile"
                title="Profile"
                subtitle="Update your account profile."
                icon={<PersonCircle />}
                accent="#38bdf8"
              />

              <SettingLink
                to="/settings/preferences"
                title="Preferences"
                subtitle="Adjust your personal dashboard settings."
                icon={<Sliders />}
                accent="#a855f7"
              />
            </SettingCard>
          </Col>

          <Col lg={6}>
            <SettingCard
              title="Calendar & Notifications"
              subtitle="Manage events, reminders, and dashboard notifications."
              icon={<CalendarEvent />}
              accent="#f59e0b"
            >
              <SettingLink
                to="/settings/calendar"
                title="Manage Events & Reminders"
                subtitle="Create calendar events and notification reminders."
                icon={<CalendarEvent />}
                accent="#f59e0b"
              />
            </SettingCard>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Settings;